import { useCallback, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Icon } from '../../atoms/Icon/Icon';
import { Popover, popoverAnchorClass } from '../../primitives/Popover/Popover';
import { ModelListItem } from '../ModelListItem/ModelListItem';
import styles from './ModelSelector.module.css';

/**
 * Figma: `Model selector` (2055:21391) — the mode picker in the Prompt
 * Composer's action row. `Type=Button` (the only type) and `Open=False|True`.
 *
 * The trigger is the `Button` atom, `Hierarchy=Ghost Color`, with the current
 * mode's icon leading and a chevron trailing — down closed, up open, which is
 * the one thing the two variants change on the trigger. The menu is a 240-wide
 * card of `_model list item` rows that opens **upward** (Figma places it at
 * y=-86, above the trigger): the composer sits at the bottom of the screen, so
 * a menu that opened downward would leave the viewport.
 *
 * `Open` is the `Popover` primitive, so Escape and pointer-down-outside close
 * it without restating either here.
 */
export type ModelOption = {
  id: string;
  /** The mode's name — what the trigger shows when it is selected. */
  label: string;
  /** One line under the name in the menu; the composer shows it as its eyebrow. */
  description: string;
  icon?: ReactNode;
};

export type ModelSelectorProps = {
  options: ModelOption[];
  /** The selected option's id. Controlled. */
  value: string;
  onValueChange?: (id: string) => void;
  /** Figma `Open`. Omit to let the component hold it. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Accessible name for the listbox. Not in Figma. */
  'aria-label'?: string;
  disabled?: boolean;
  className?: string;
};

export function ModelSelector({
  options,
  value,
  onValueChange,
  open,
  defaultOpen = false,
  onOpenChange,
  'aria-label': ariaLabel = 'Mode',
  disabled = false,
  className,
}: ModelSelectorProps) {
  const listId = useId();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isOpen = (open ?? uncontrolledOpen) && !disabled;

  const setOpen = useCallback(
    (next: boolean) => {
      if (open === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [open, onOpenChange],
  );

  const rowsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.id === value),
  );
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const selected = options[selectedIndex];

  const focusRow = (index: number) => {
    setActiveIndex(index);
    rowsRef.current[index]?.focus();
  };

  const openWith = (index: number) => {
    setOpen(true);
    setActiveIndex(index);
    // The panel mounts this frame; focus it once it exists.
    requestAnimationFrame(() => rowsRef.current[index]?.focus());
  };

  const close = useCallback(
    (returnFocus: boolean) => {
      setOpen(false);
      if (returnFocus) triggerRef.current?.focus();
    },
    [setOpen],
  );

  /*
   * Keyboard, past Figma: the trigger opens on Enter, Space and either arrow —
   * landing on the selected row rather than the first, which is what a
   * single-select control does — and the rows move with the arrows, Home and
   * End. Tab leaves and closes; Escape closes and returns focus.
   */
  const onTriggerKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (options.length === 0) return;
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      openWith(selectedIndex);
    }
  };

  const onListKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (options.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusRow((activeIndex + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusRow((activeIndex - 1 + options.length) % options.length);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusRow(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusRow(options.length - 1);
    } else if (event.key === 'Tab') {
      close(false);
    }
  };

  const onListKeyDownCapture = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'Escape') close(true);
  };

  const choose = (option: ModelOption) => {
    onValueChange?.(option.id);
    close(true);
  };

  return (
    <div className={[styles.root, popoverAnchorClass, className].filter(Boolean).join(' ')}>
      <Button
        ref={triggerRef}
        hierarchy="ghostColor"
        iconLeading={selected?.icon}
        iconTrailing={<Icon name={isOpen ? 'chevronUp' : 'chevronDown'} />}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listId : undefined}
        disabled={disabled}
        onClick={() => (isOpen ? close(false) : openWith(selectedIndex))}
        onKeyDown={onTriggerKeyDown}
      >
        {selected?.label}
      </Button>

      <Popover
        open={isOpen}
        onClose={() => close(false)}
        placement="top-start"
        surface={false}
        role="listbox"
        label={ariaLabel}
        id={listId}
        className={styles.menu}
      >
        {/* The panel is the listbox; a `<ul>` inside it is presentational. */}
        <ul
          role="none"
          className={styles.list}
          onKeyDown={onListKeyDown}
          onKeyDownCapture={onListKeyDownCapture}
        >
          {options.map((option, index) => (
            /* Figma `Menu items` — one 2px-padded wrapper per row. */
            <li key={option.id} role="none" className={styles.menuItems}>
              <ModelListItem
                ref={(node: HTMLButtonElement | null) => {
                  rowsRef.current[index] = node;
                }}
                label={option.label}
                description={option.description}
                icon={option.icon}
                selected={option.id === value}
                onSelect={() => choose(option)}
                tabIndex={index === activeIndex ? 0 : -1}
              />
            </li>
          ))}
        </ul>
      </Popover>
    </div>
  );
}
